import { Injectable, inject } from '@angular/core';
import { App } from '@df/core/app';
import { Logger } from '@df/core/service/logger';
import { Url } from '@df/core/service/url';
import { IResizeOptions, ResizeService } from '@df/ui/common/resize.service';
import defaults from 'lodash-es/defaults';
import { NCloudflare } from './cloudflare.interface';

/**
 * Documentation with URL image parameters
 * https://developers.cloudflare.com/images/image-resizing/resize-with-workers
 */
@Injectable({
    providedIn: 'root'
})
export class CloudflareResizeService extends ResizeService {
    private logger = inject(Logger);
    private url = inject(Url);
    private cloudflareConfig = inject(NCloudflare.OPTIONS);

    private mediaHost = inject(App).dfConfig.endpoint.media;

    /**
     * Image loader for `DfImageDirective`
     *
     * @param config {NCloudflare.ILoaderConfig}
     * @returns {string}
     *
     * @link https://angular.dev/guide/image-optimization#configuring-an-image-loader-for-ngoptimizedimage
     */
    loader(config: NCloudflare.ILoaderConfig): string {
        if (!config.src) {
            return '';
        }

        if (config.src?.match(/\.(svg|gif)$/)) {
            return this.url.toMediaUrl(config.src);
        }

        if (config.width) {
            config.loaderParams = config.loaderParams || ({} as IResizeOptions);
            config.loaderParams.width = config.width;
        }

        return this.resize(this.url.toMediaUrl(config.src), config.loaderParams);
    }

    /**
     * Modifies the image URL with the appropriate resize parameters
     *
     * @param imageUrl {string} The URL of the image
     * @param options {IResizeOptions} Resize options
     * @returns {string} The modified image URL
     */
    resize(imageUrl: string, options: IResizeOptions): string {
        options = defaults(options, this.cloudflareConfig.defaultOptions);

        const args: string[] = [];

        const imageOptions: string[] = [];

        const newWidth = options.strictDims ? options.width : this.round(options.width);

        args.push(this.mediaHost, 'cdn-cgi', 'image');

        imageOptions.push('dpr=1');

        if (options.width) {
            imageOptions.push('w=' + newWidth);
        }

        if (options.height) {
            imageOptions.push(
                'h=' +
                    (options.strictDims
                        ? options.height
                        : options.width
                          ? Math.round((newWidth * options.height) / options.width)
                          : Math.round(options.height))
            );
        }

        if (options.trim) {
            this.logger.warn('Trim options is not supported with cloudflare');
        }

        if (options.fitIn && options.fitIn !== '0') {
            imageOptions.push('fit=pad');
        } else if (options.upscale) {
            imageOptions.push('fit=cover');
        } else {
            imageOptions.push('fit=scale-down');
        }

        if (options.cropType) {
            imageOptions.push('g=' + options.cropType);
        }

        if (options.horizontalCrop) {
            imageOptions.push('x=' + options.horizontalCrop);
        }

        if (options.verticalCrop) {
            imageOptions.push('y=' + options.verticalCrop);
        }

        if (options.sharpen) {
            imageOptions.push('sharpen=' + options.sharpen);
        }

        if (options.format) {
            imageOptions.push('f=' + options.format);
        } else {
            imageOptions.push('f=auto');
        }

        if (options.quality) {
            imageOptions.push('q=' + options.quality);
        }

        if (options.fillColor) {
            imageOptions.push('background=' + options.fillColor);
        }

        args.push(imageOptions.join(','));

        // source
        if (this.mediaHost) {
            args.push(imageUrl.replace(this.mediaHost, '').replace(/^(\/)+/, ''));
        } else {
            args.push(imageUrl.replace(/^(\/)+/, ''));
        }

        return args.join('/');
    }

    private round(width: number): number {
        const steps = [50, 100, 200, 400, 800];
        let i = 0;
        let newWidth = 0;
        do {
            newWidth = steps[i++] || newWidth + 600;
        } while (newWidth < width);
        return newWidth;
    }
}
